/**
 * IncidentCreate — admin form to file a new incident report.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import BodyMap from "../../components/BodyMap";

export default function IncidentCreate() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [children, setChildren] = useState([]);
  const [form, setForm] = useState({
    child_id: "",
    incident_type: "injury",
    severity: "low",
    occurred_at: new Date().toISOString().slice(0, 16),
    location_text: "",
    body_map_locations: [],
    description: "",
    action_taken: "",
    staff_notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/children");
        setChildren(r.data || []);
        if (r.data?.length) setForm((f) => ({ ...f, child_id: r.data[0].id }));
      } catch {
        // ignore
      }
    })();
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.description.trim() || !form.action_taken.trim()) {
      toast.error("Please describe what happened and what was done.");
      return;
    }
    setSubmitting(true);
    try {
      const occurred_iso = form.occurred_at.length === 16 ? `${form.occurred_at}:00` : form.occurred_at;
      const r = await api.post("/admin/ops/incidents", {
        ...form,
        occurred_at: new Date(occurred_iso).toISOString(),
        witnesses: [],
      });
      toast.success("Incident report filed.");
      nav(`/admin/ops/incidents/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ops.errors.generic"));
    }
    setSubmitting(false);
  };

  return (
    <main className="p-6 lg:p-10 max-w-3xl mx-auto" data-testid="admin-incident-create">
      <Link
        to="/admin/ops/incidents"
        className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#1A1B2E] mb-6"
      >
        <ArrowLeft size={14} strokeWidth={1.6} />
        {t("ops.incidents.detail_back")}
      </Link>

      <span className="eyebrow">{t("ops.incidents.eyebrow")}</span>
      <h1 className="mt-2 font-display text-3xl text-[#1A1B2E]">New incident report.</h1>

      <form onSubmit={submit} className="mt-8 space-y-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="eyebrow">{t("ops.incidents.form_child")}</span>
            <select
              value={form.child_id}
              onChange={(e) => setForm({ ...form, child_id: e.target.value })}
              data-testid="incident-form-child"
              className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm bg-white focus:outline-none focus:border-[#1F3A2A]"
            >
              {children.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="eyebrow">{t("ops.incidents.form_type")}</span>
            <select
              value={form.incident_type}
              onChange={(e) => setForm({ ...form, incident_type: e.target.value })}
              data-testid="incident-form-type"
              className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm bg-white focus:outline-none focus:border-[#1F3A2A]"
            >
              {["injury", "illness", "behavior", "safety", "near_miss", "other"].map((k) => (
                <option key={k} value={k}>{t(`ops.incidents.type_${k}`)}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="eyebrow">{t("ops.incidents.form_severity")}</span>
            <select
              value={form.severity}
              onChange={(e) => setForm({ ...form, severity: e.target.value })}
              data-testid="incident-form-severity"
              className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm bg-white focus:outline-none focus:border-[#1F3A2A]"
            >
              {["low", "medium", "high", "critical"].map((s) => (
                <option key={s} value={s}>{t(`ops.incidents.severity_${s}`)}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="eyebrow">{t("ops.incidents.form_occurred_at")}</span>
            <input
              type="datetime-local"
              value={form.occurred_at}
              onChange={(e) => setForm({ ...form, occurred_at: e.target.value })}
              data-testid="incident-form-occurred-at"
              className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="eyebrow">{t("ops.incidents.form_location")}</span>
            <input
              type="text"
              value={form.location_text}
              onChange={(e) => setForm({ ...form, location_text: e.target.value })}
              data-testid="incident-form-location"
              className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
            />
          </label>
        </div>

        <div>
          <span className="eyebrow">{t("ops.incidents.form_body_map")}</span>
          <div className="mt-3 rounded-2xl border border-[#EEF0F4] bg-[#FAF9F1]/40 p-4">
            <BodyMap
              selected={form.body_map_locations}
              onChange={(next) => setForm({ ...form, body_map_locations: next })}
            />
          </div>
        </div>

        <label className="block">
          <span className="eyebrow">{t("ops.incidents.form_description")}</span>
          <textarea
            rows={4}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="incident-form-description"
            className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
          />
        </label>
        <label className="block">
          <span className="eyebrow">{t("ops.incidents.form_action_taken")}</span>
          <textarea
            rows={4}
            value={form.action_taken}
            onChange={(e) => setForm({ ...form, action_taken: e.target.value })}
            data-testid="incident-form-action"
            className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
          />
        </label>
        <label className="block">
          <span className="eyebrow">{t("ops.incidents.form_staff_notes")}</span>
          <textarea
            rows={2}
            value={form.staff_notes}
            onChange={(e) => setForm({ ...form, staff_notes: e.target.value })}
            data-testid="incident-form-staff-notes"
            className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
          />
        </label>

        <div className="flex items-center justify-end">
          <button
            type="submit"
            disabled={submitting}
            data-testid="incident-form-submit"
            className={`inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-5 py-2.5 hover:bg-[#132C17] ${submitting ? "opacity-60" : ""}`}
          >
            {t("ops.incidents.submit_create")}
          </button>
        </div>
      </form>
    </main>
  );
}
